import React from "react";
import { Row, Col, Form, Input, InputNumber, Select, Switch } from "antd";

export const LANGUAGES = [
  { key: "en", label: "English" },
  { key: "ms", label: "Malay" },
  { key: "zh", label: "Chinese" },
];

export const COPY_FIELDS = [
  {
    name: "name",
    label: "Package name",
    maxLength: 20,
    hint: "Shown as the package title on the merchant promo page.",
  },
  {
    name: "description",
    label: "Description",
    maxLength: 60,
    hint: "One-line description under the package title.",
  },
  {
    name: "voucherText",
    label: "Voucher line",
    hint: "Free gifts row, above the campaign bonus line.",
  },
  {
    name: "supportText",
    label: "Customer support line",
    hint: 'Support row, e.g. "First 30 Days".',
  },
];

const NUMERIC_FIELDS = [
  {
    name: "amount",
    label: "Qualifying top-up (RM)",
    min: 1,
    hint: "Merchants topping up this amount or more get this package. Also sets the display order.",
  },
  {
    name: "promoRateDurationDays",
    label: "Promo rate duration (days)",
    min: 1,
    integer: true,
  },
  {
    name: "trackingDurationDays",
    label: "Tracking notification (days)",
    min: 1,
    integer: true,
  },
  { name: "freeFlyers", label: "Free flyers", min: 0, integer: true },
  {
    name: "topupValidDurations",
    label: "Credit validity (days)",
    min: 1,
    integer: true,
    hint: "How long the granted top-up credit stays valid before expiring.",
  },
];

export const toFormValues = (record) => {
  const values = {
    amount: record.amount,
    promoRateDurationDays: record.promoRateDurationDays,
    trackingDurationDays: record.trackingDurationDays,
    freeFlyers: record.freeFlyers,
    topupValidDurations: record.topupValidDurations,
    pricingPackage: record.pricingPackage,
    feePackage: record.feePackage,
    isPopular: record.isPopular,
  };

  COPY_FIELDS.forEach(({ name }) =>
    LANGUAGES.forEach(({ key }) => {
      values[`${name}_${key}`] = record[name]?.[key];
    })
  );

  return values;
};

export const toPayload = (values) => {
  const payload = {
    amount: values.amount,
    promoRateDurationDays: values.promoRateDurationDays,
    trackingDurationDays: values.trackingDurationDays,
    freeFlyers: values.freeFlyers,
    topupValidDurations: values.topupValidDurations,
    pricingPackage: values.pricingPackage,
    feePackage: values.feePackage,
    isPopular: values.isPopular ?? false,
  };

  if (values.isActive !== undefined) {
    payload.isActive = values.isActive;
  }

  COPY_FIELDS.forEach(({ name }) => {
    payload[name] = LANGUAGES.reduce(
      (translations, { key }) => ({
        ...translations,
        [key]: values[`${name}_${key}`],
      }),
      {}
    );
  });

  return payload;
};

const WelcomePackageFields = ({ pricingPackageOptions, feePackageOptions }) => (
  <>
    <Row gutter={16}>
      {NUMERIC_FIELDS.map(({ name, label, min, hint, integer }) => (
        <Col xs={24} sm={12} md={6} key={name}>
          <Form.Item
            name={name}
            label={label}
            tooltip={hint}
            rules={[
              { required: true, message: `${label} is required` },
              {
                type: integer ? "integer" : "number",
                min,
                message: integer
                  ? `Must be a whole number of at least ${min}`
                  : `Must be at least ${min}`,
              },
            ]}
          >
            <InputNumber
              min={min}
              precision={integer ? 0 : undefined}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
      ))}
    </Row>

    <Row gutter={16}>
      <Col xs={24} sm={12} md={8}>
        <Form.Item
          name="pricingPackage"
          label="Pricing package"
          tooltip="Rate plan granted to the merchant. Its cheapest rate is what the promo page shows as the domestic rate."
          rules={[{ required: true, message: "Pricing package is required" }]}
        >
          <Select showSearch optionFilterProp="children" placeholder="Select pricing package">
            {pricingPackageOptions.map((option) => (
              <Select.Option key={option.key} value={option.key}>
                {option.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} md={8}>
        <Form.Item
          name="feePackage"
          label="Fee package"
          tooltip="Governs tracking email and SMS fees."
          rules={[{ required: true, message: "Fee package is required" }]}
        >
          <Select showSearch optionFilterProp="children" placeholder="Select fee package">
            {feePackageOptions.map((option) => (
              <Select.Option key={option.key} value={option.key}>
                {option.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} md={8}>
        <Form.Item
          name="isPopular"
          label="Show Popular tag"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Col>
    </Row>

    {COPY_FIELDS.map(({ name, label, maxLength, hint }) => (
      <Row gutter={16} key={name}>
        {LANGUAGES.map(({ key, label: language }) => (
          <Col xs={24} sm={8} key={`${name}_${key}`}>
            <Form.Item
              name={`${name}_${key}`}
              label={`${label} (${language})`}
              tooltip={key === "en" ? hint : undefined}
              rules={[
                { required: true, message: `${label} (${language}) is required` },
                ...(maxLength
                  ? [{ max: maxLength, message: `Maximum ${maxLength} characters` }]
                  : []),
              ]}
            >
              <Input maxLength={maxLength} />
            </Form.Item>
          </Col>
        ))}
      </Row>
    ))}
  </>
);

export default WelcomePackageFields;
